import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '~addListing/types';
import LocationSearch from '~addListing/pages/InitializeStep/Location/LocationSearch';
import { useGetLocationsMutation } from '~/redux/api/userApiSlice';
import Skeleton from 'react-loading-skeleton';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import SubLocationSearch from '~addListing/pages/InitializeStep/Location/SubLocationSearch';

export type LocationSearchType = {
  location: number | null;
  centerLat: number;
  centerLng: number;
};

const Location = ({
  errors,
  setValue,
  clearErrors,
  allowSelectAllLocations,
  hideSuggestLocation,
  onSelectLocation,
}: {
  setValue: UseFormSetValue<LocationSearchType>;
  errors: FieldErrors<LocationSearchType>;
  clearErrors: UseFormClearErrors<LocationSearchType>;
  allowSelectAllLocations?: boolean;
  hideSuggestLocation?: boolean;
  onSelectLocation?: (location: LocationType | null) => void;
}) => {
  const { t } = useTranslation('app');
  const [selectedLocations, setSelectedLocations] = useState<
    { index: number; location: LocationType | null }[]
  >([
    {
      index: 0,
      location: null,
    },
  ]);

  const [getLocations, { isLoading }] = useGetLocationsMutation();

  const handleIsLocationHasChildren = useCallback(
    async (location: LocationType) => {
      const res = await getLocations(
        new URLSearchParams({
          locationParent: location?.id?.toString() || '',
        }).toString(),
      );
      if ('data' in res && res.data.length > 0) {
        setValue('location', null);
        onSelectLocation?.(null);
        setSelectedLocations((prev) => [
          ...prev,
          {
            index: prev.length,
            location: null,
          },
        ]);
      } else {
        setValue('location', location?.id);
        onSelectLocation?.(location);
      }
    },
    [onSelectLocation, selectedLocations],
  );

  const handleSelectLocation = (
    location: LocationType | null,
    index: number,
  ) => {
    const newSelectedLocations = selectedLocations.map((selectedLocation) => {
      if (selectedLocation.index === index) {
        return { index, location };
      }
      return selectedLocation;
    });
    setValue('centerLat', Number(location?.center_lat));
    setValue('centerLng', Number(location?.center_lng));

    newSelectedLocations.splice(index + 1);

    setSelectedLocations(newSelectedLocations);

    if (location) handleIsLocationHasChildren(location);
  };

  const handleResetLocation = () => {
    onSelectLocation?.(null);
    setSelectedLocations([
      {
        index: 0,
        location: null,
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-1.5x z-[9]">
      <p className="text-title_4 flex flex-col gap-2x">{t('location')}</p>
      {selectedLocations.map((location, index) =>
        location.location === null && index !== 0 ? (
          <SubLocationSearch
            parentLocation={selectedLocations[index - 1]?.location}
            location={location.location}
            handleSelectLocation={(location) =>
              handleSelectLocation(location, index)
            }
            searchMode="children"
            errors={errors}
            clearErrors={clearErrors}
            allowSelectAllLocations={allowSelectAllLocations}
            hideSuggestLocation={hideSuggestLocation}
          />
        ) : (
          <LocationSearch
            handleSelectMainLocation={handleSelectLocation}
            location={location.location}
            selectedLocationIndex={location.index}
            parentLocation={selectedLocations[index - 1]?.location}
            key={location.location?.title}
            handleResetLocation={handleResetLocation}
            errors={selectedLocations.length === 1 ? errors : {}}
            clearErrors={clearErrors}
            setValue={setValue}
            allowSelectAllLocations={allowSelectAllLocations}
            hideSuggestLocation={hideSuggestLocation}
          />
        ),
      )}
      {selectedLocations[0].location && isLoading && (
        <Skeleton height={40} width="100%" borderRadius={12} />
      )}
    </div>
  );
};

export default Location;
